import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { COLORS } from "../config/carto";
import {
  fetchIsochrones,
  fetchPopulationInIsochrones,
} from "../layers/fetchLayers";
import "../styles/popup.css";

interface Props {
  time: number;
  airportStatus: string[];
  showPop: boolean;
  setCount: (n: number) => void;
  setTotalPop: (n: number) => void;
  setLoading: (b: boolean) => void;
  setError: (s: string | null) => void;
  initialZoom?: number;
  scrollWheelZoom?: boolean;
  onAirportsLoaded?: (airports: { id: string; name: string; lat: number; lng: number }[]) => void;
  onMapReady?: (map: L.Map) => void;
}

// Helper pour calculer le centroïde d'un polygone
function getCentroid(geom: any): { lat: number; lng: number } | null {
  try {
    let coords = geom.type === 'Polygon' ? geom.coordinates[0] : geom.coordinates;
    if (!coords || coords.length === 0) return null;
    let latSum = 0, lngSum = 0, count = 0;
    for (const c of coords) {
      if (c.length >= 2) {
        latSum += c[1];
        lngSum += c[0];
        count++;
      }
    }
    if (count === 0) return null;
    return { lat: latSum / count, lng: lngSum / count };
  } catch { return null; }
}

export default function Map({
  time, airportStatus, showPop,
  setCount, setTotalPop, setLoading, setError,
  initialZoom = 5.4,
  scrollWheelZoom = true,
  onAirportsLoaded,
  onMapReady,
}: Props) {
  const mapRef = useRef<L.Map | null>(null);
  const isoLayerRef = useRef<L.GeoJSON | null>(null);
  const fetchKeyRef = useRef(0);

  // ── INITIALISATION DE LA CARTE (position et zoom fixes) ──
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map("map", {
      center: [22, 70],
      zoom: 5.4,
      zoomControl: false,
      preferCanvas: true,
      scrollWheelZoom,
    });
    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      { 
        attribution: "© CARTO",
        crossOrigin: true,
      }
    ).addTo(map);
    mapRef.current = map;
    if (onMapReady) onMapReady(map);
  }, []);

  // ── CHARGEMENT DES ISOCHRONES ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    fetchKeyRef.current += 1;
    const myKey = fetchKeyRef.current;

    if (isoLayerRef.current) {
      map.removeLayer(isoLayerRef.current);
      isoLayerRef.current = null;
    }
    setCount(0);
    setTotalPop(0);

    if (airportStatus.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const currentTime = time;
    const currentStatus = [...airportStatus];

    Promise.all([
      fetchIsochrones(currentTime, currentStatus),
      fetchPopulationInIsochrones(currentTime, currentStatus),
    ])
      .then(([isoRows, popRows]) => {
        if (myKey !== fetchKeyRef.current) return;
        const map = mapRef.current;
        if (!map) return;

        if (isoLayerRef.current) {
          map.removeLayer(isoLayerRef.current);
          isoLayerRef.current = null;
        }

        const total = (popRows || []).reduce((acc: number, r: any) => acc + (r.population || 0), 0);
        setTotalPop(total);

        if (!isoRows || isoRows.length === 0) {
          setError("No isochrone data");
          setLoading(false);
          return;
        }

        setCount(isoRows.length);

        // ── EXTRACTION DES AÉROPORTS ──
        if (onAirportsLoaded) {
          const airports: { id: string; name: string; lat: number; lng: number }[] = isoRows
            .filter((r: any) => r.isochrone_geojson)
            .map((r: any) => {
              let geom = null;
              try {
                const geojson = r.isochrone_geojson;
                if (geojson?.type === 'FeatureCollection' && geojson.features?.length > 0) {
                  geom = geojson.features[0].geometry;
                } else if (geojson?.type === 'Feature') {
                  geom = geojson.geometry;
                }
              } catch (e) {}
              
              if (!geom) return null;
              
              const centroid = getCentroid(geom);
              return {
                id: r.airport_id,
                name: r.airport_name || r.airport_id,
                lat: centroid?.lat || 0,
                lng: centroid?.lng || 0,
              };
            })
            .filter((a: any) => a && a.lat !== 0 && a.lng !== 0);
          
          const unique = airports.filter(
            (a, index, self) => self.findIndex((b) => b.id === a.id) === index
          );
          onAirportsLoaded(unique);
        }

        // ── CONSTRUCTION DU GEOJSON ──
        const features = isoRows
          .filter((r: any) => r.isochrone_geojson)
          .map((r: any) => {
            let geometry = null;
            try {
              const geojson = r.isochrone_geojson;
              if (geojson?.type === 'FeatureCollection' && geojson.features?.length > 0) {
                geometry = geojson.features[0].geometry;
              } else if (geojson?.type === 'Feature') {
                geometry = geojson.geometry;
              } else {
                geometry = geojson;
              }
            } catch (e) {}
            
            if (!geometry) return null;
            
            return {
              type: "Feature" as const,
              geometry: geometry,
              properties: {
                airport_type: r.airport_type,
                airport_name: r.airport_name || r.airport_id,
                airport_id: r.airport_id,
                drive_time: currentTime,
              },
            };
          })
          .filter((f: any) => f !== null);

        if (features.length === 0) {
          setError("No valid isochrone geometry");
          setLoading(false);
          return;
        }

        const geojson = {
          type: "FeatureCollection" as const,
          features: features,
        };

        // ── AJOUT À LA CARTE ──
        isoLayerRef.current = L.geoJSON(geojson, {
          style: (f) => ({
            color: COLORS[f?.properties.airport_type] || "#fff",
            fillColor: COLORS[f?.properties.airport_type] || "#fff",
            weight: 2,
            fillOpacity: 0.12,
            opacity: 1,
          }),
          onEachFeature: (f, layer) => {
            const { airport_name, airport_id, airport_type, drive_time } = f.properties;
            const color = COLORS[airport_type] || "#fff";
            const path = layer as L.Path;

            const popupContent = `
              <div style="font-family:'ATR Innova',sans-serif;min-width:220px;padding:4px">
                <div style="font-size:8px;letter-spacing:2px;color:rgba(255,255,255,0.35);text-transform:uppercase;margin-bottom:10px;display:flex;align-items:center;gap:7px">
                  <div style="width:7px;height:7px;border-radius:50%;background:${color};box-shadow:0 0 8px ${color}"></div>
                  ${airport_type}
                </div>
                <div style="font-size:18px;font-weight:800;color:white;letter-spacing:-0.5px;line-height:1.1;margin-bottom:3px">${airport_name}</div>
                <div style="font-size:10px;color:rgba(255,255,255,0.25);margin-bottom:14px;font-family:monospace;letter-spacing:0.5px">${airport_id}</div>
                <div style="height:1px;background:rgba(255,255,255,0.06);margin-bottom:12px"></div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
                  <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.06);border-radius:8px;padding:10px 12px">
                    <div style="font-size:8px;letter-spacing:1.5px;color:rgba(255,255,255,0.25);text-transform:uppercase;margin-bottom:5px">Drive time</div>
                    <div style="font-size:20px;font-weight:800;color:${color};letter-spacing:-0.5px;line-height:1">${drive_time}<span style="font-size:10px;font-weight:400;color:rgba(255,255,255,0.3)"> min</span></div>
                  </div>
                  <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.06);border-radius:8px;padding:10px 12px">
                    <div style="font-size:8px;letter-spacing:1.5px;color:rgba(255,255,255,0.25);text-transform:uppercase;margin-bottom:5px">Status</div>
                    <div style="font-size:12px;font-weight:700;color:white;line-height:1.3">${airport_type}</div>
                  </div>
                </div>
              </div>
            `;

            layer.bindPopup(popupContent, {
              maxWidth: 300,
              className: "dark-popup",
              autoPan: false,
            });

            layer.on("mouseover", (e) => {
              path.setStyle({ fillOpacity: 0.2, weight: 3 });
              path.bringToFront();
              layer.openPopup(e.latlng);
            });
            layer.on("mousemove", (e) => {
              layer.openPopup(e.latlng);
            });
            layer.on("mouseout", () => {
              isoLayerRef.current?.resetStyle(path);
              layer.closePopup();
            });
          },
        }).addTo(map);

        setLoading(false);
      })
      .catch((e) => {
        if (myKey === fetchKeyRef.current) {
          setError(e.message || "Error loading data");
          setLoading(false);
        }
      });
  }, [time, airportStatus]);

  return (
    <div id="map-container" style={{ width: "100%", height: "100%", position: "relative" }}>
      <div id="map" style={{ width: "100%", height: "100%" }} />
      
      {/* ── BOUTONS DE ZOOM (style New Moon) ── */}
      <div style={{
        position: "absolute",
        top: 72, right: 20,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}>
        {[
          { title: "Zoom in", icon: <path d="M12 5v14M5 12h14"/>, action: () => mapRef.current?.zoomIn() },
          { title: "Zoom out", icon: <path d="M5 12h14"/>, action: () => mapRef.current?.zoomOut() },
          {
            title: "Reset view",
            icon: <><path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8"/><path d="M3 3v5h5"/></>,
            action: () => mapRef.current?.setView([22, 70], 5.4, { animate: true })
          },
        ].map((btn, i) => (
          <button
            key={i}
            title={btn.title}
            onClick={btn.action}
            style={{
              width: 36,
              height: 36,
              background: "rgba(0, 49, 60, 0.92)",        /* New Moon */
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 8,
              color: "rgba(255, 255, 255, 0.7)",          /* Blanc lumineux */
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(12px)",
              transition: "all 0.15s",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = "rgba(0, 154, 132, 0.2)";
              el.style.color = "white";
              el.style.borderColor = "rgba(0, 154, 132, 0.3)";
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = "rgba(0, 49, 60, 0.92)";
              el.style.color = "rgba(255, 255, 255, 0.7)";
              el.style.borderColor = "rgba(255, 255, 255, 0.08)";
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              {btn.icon}
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}